import { useEffect, useMemo, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import Select from 'react-select'
import Swal from 'sweetalert2'
import { useThemeMode } from '../../../_metronic/partials'
import { SelectStyles } from '../../Select'
import {
  bulkTransferClients,
  getTransferTargets,
  TransferTarget,
} from '../../../app/modules/settings/api/requisiteClientApi'
import type { BulkTransferModalProps } from './types'
import { getErrorText } from '../../../app/common/api/errors'

type TargetOption = { value: number; label: string; target: TransferTarget }

const BulkTransferModal = ({
  open,
  sourceRequisiteId,
  onClose,
  onSuccess,
}: BulkTransferModalProps) => {
  const { mode: theme } = useThemeMode()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [targets, setTargets] = useState<TransferTarget[]>([])
  const [globalLimit, setGlobalLimit] = useState<number>(500)
  const [sourceClientsCount, setSourceClientsCount] = useState<number>(0)
  const [selectedOption, setSelectedOption] = useState<TargetOption | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError(null)
    setSelectedOption(null)
    getTransferTargets(sourceRequisiteId)
      .then((res) => {
        if (res.data.success && res.data.data) {
          setTargets(res.data.data.targets)
          setGlobalLimit(res.data.data.globalLimit)
          setSourceClientsCount(res.data.data.sourceClientsCount)
        } else {
          setError('Не удалось загрузить список реквизитов')
        }
      })
      .catch(() => setError('Ошибка при загрузке списка реквизитов'))
      .finally(() => setLoading(false))
  }, [open, sourceRequisiteId])

  const options = useMemo<TargetOption[]>(() =>
    targets.map((t) => ({
      value: t.id,
      label: `#${t.id} ${t.name} (${t.currentClientsCount} кл.${t.fastTrackEnabled ? `, Fast-track: ${t.fastTrackMaxClients}` : ''})`,
      target: t,
    })),
    [targets]
  )

  const computation = useMemo(() => {
    if (!selectedOption) return null
    const { target } = selectedOption
    const newAssignments = sourceClientsCount - target.intersectionCount
    const effective = target.currentClientsCount + newAssignments
    const overGlobal = effective > globalLimit
    const overFastTrack = target.fastTrackEnabled && effective > target.fastTrackMaxClients
    return { newAssignments, effective, overGlobal, overFastTrack }
  }, [selectedOption, sourceClientsCount, globalLimit])

  const canSubmit = !!selectedOption && !submitting && computation !== null &&
    !computation.overGlobal && !computation.overFastTrack

  const handleSubmit = async () => {
    if (!selectedOption) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await bulkTransferClients(sourceRequisiteId, selectedOption.value)
      if (res.data.success && res.data.data) {
        await Swal.fire({
          toast: true, position: 'top', icon: 'success',
          title: `Перенесено ${res.data.data.transferredCount} клиентов`,
          showConfirmButton: false, timer: 2500,
        })
        onSuccess(res.data.data.transferredCount)
        onClose()
      } else {
        setError(getErrorText(res, 'Не удалось выполнить перенос'))
      }
    } catch (e: unknown) {
      // The interceptor rejects with the response itself; this URL is out of its alerts, so this is the only message.
      setError(getErrorText(e, 'Ошибка при выполнении переноса'))
    } finally {
      setSubmitting(false)
    }
  }

  const selected = selectedOption?.target ?? null

  return (
    <Modal show={open} onHide={onClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Массовый перенос клиентов</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='text-center py-5'>Загрузка...</div>
        ) : (
          <>
            <div className='fv-row mb-5'>
              <label className='fs-6 fw-semibold mb-2'>Целевой реквизит</label>
              <Select
                styles={SelectStyles(theme)}
                placeholder='Поиск по названию или ID...'
                isClearable
                isSearchable
                options={options}
                value={selectedOption}
                onChange={(opt) => setSelectedOption(opt as TargetOption | null)}
                noOptionsMessage={() => 'Совместимых реквизитов не найдено'}
                isDisabled={submitting}
              />
            </div>

            {selected && computation && (
              <div className='card border bg-light p-3 mb-3'>
                <div>На источнике: <b>{sourceClientsCount}</b> клиентов</div>
                <div>На целевом сейчас: <b>{selected.currentClientsCount}</b> / {globalLimit}</div>
                <div>Уже на целевом: <b>{selected.intersectionCount}</b> (останутся как есть)</div>
                <div>Будет создано новых привязок: <b>{computation.newAssignments}</b></div>
                <div className={computation.overGlobal ? 'text-danger' : ''}>
                  После переноса: <b>{computation.effective}</b> / {globalLimit}
                </div>
                {selected.fastTrackEnabled && (
                  <div className={computation.overFastTrack ? 'text-danger' : ''}>
                    Fast-track лимит: <b>{computation.effective}</b> / {selected.fastTrackMaxClients}
                  </div>
                )}
                {computation.overGlobal && (
                  <div className='text-danger mt-2'>Превышен общий лимит ({globalLimit})</div>
                )}
                {computation.overFastTrack && (
                  <div className='text-danger mt-2'>
                    Превышен Fast-track лимит ({selected.fastTrackMaxClients})
                  </div>
                )}
              </div>
            )}

            {error && <div className='alert alert-danger'>{error}</div>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={submitting}>
          Отмена
        </Button>
        <Button variant='primary' onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Выполняется...' : 'Подтвердить'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default BulkTransferModal
